import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PdfExportButton } from "@/components/PdfExportButton";
import { LongWeekendTable, FaqJsonLd, CrossLinks } from "@/components/pages/PseoExtras";
import { parseISO, calendarDatePath, calendarYearPath, dayOfWeek } from "@/lib/calendar/dates";
import { getHolidaysForYear, isEstimatedYear, SUPPORTED_YEARS, DISCLAIMER_SKB } from "@/data/holidays";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import type { Locale } from "@/i18n/types";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";

export function TanggalMerahPage({ y, locale }: { y: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <Body y={y} />
    </SiteShell>
  );
}

function Body({ y }: { y: number }) {
  const { t, href, locale } = useLocale();
  const holidays = getHolidaysForYear(y);
  const libur = holidays.filter((h) => h.type === "libur");
  const cuti = holidays.filter((h) => h.type === "cuti-bersama");
  const longWeekends = libur.filter((h) => {
    const p = parseISO(h.date)!;
    const dow = dayOfWeek(p.y, p.m, p.d);
    return dow === 1 || dow === 5;
  });
  const faq =
    locale === "id"
      ? [
          { q: `Berapa banyak tanggal merah ${y}?`, a: `Tahun ${y} memiliki ${libur.length} libur nasional dan ${cuti.length} cuti bersama, ditambah hari Minggu sepanjang tahun.` },
          { q: `Apa itu tanggal merah?`, a: `Tanggal merah adalah hari libur resmi: meliputi libur nasional, cuti bersama, dan hari Minggu.` },
          { q: `Kapan long weekend ${y}?`, a: `Lihat daftar long weekend dan harpitnas pada tabel di halaman ini.` },
        ]
      : [
          { q: `How many red dates in ${y}?`, a: `${y} has ${libur.length} public holidays and ${cuti.length} joint leave days, plus every Sunday.` },
          { q: `What counts as a "red date"?`, a: `A red date is any official non-working day: public holidays, joint leave, and Sundays.` },
          { q: `When are long weekends in ${y}?`, a: `See the long-weekend table on this page.` },
        ];

  return (
    <>
      <FaqJsonLd items={faq} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">{t.common.home}</Link> /{" "}
        <Link to={href(calendarYearPath(y)) as never} className="hover:text-primary">{y}</Link> / {t.common.redDates}
      </nav>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.common.redDates} {y}</h1>
          <p className="mt-1 text-muted-foreground">
            {t.common.redCount(libur.length, cuti.length)}
            {isEstimatedYear(y) && <span className="ml-2 rounded-md bg-muted px-2 py-0.5 text-xs">{t.common.estimatedData}</span>}
          </p>
        </div>
        <FeaturedSvg
          variant="red-dates"
          title={`${t.common.redDates} ${y}`}
          subtitle={t.common.redCount(libur.length, cuti.length)}
          badge={String(y)}
          locale={locale}
          className="hidden md:block md:w-72"
        />
        <PdfExportButton mode="year" year={y} />
      </header>

      <article className="prose prose-sm max-w-none mb-6 text-muted-foreground">
        <p>
          {locale === "id" ? (
            <>Daftar lengkap <strong>tanggal merah {y}</strong> di Indonesia mencakup libur nasional, cuti bersama yang ditetapkan SKB 3 Menteri, serta hari Minggu sepanjang tahun.</>
          ) : (
            <>Complete list of <strong>{y} red dates</strong> in Indonesia: public holidays, joint leave from the joint ministerial decree, plus every Sunday.</>
          )}
        </p>
      </article>

      <section className="mb-8">
        <h2 className="mb-3 text-xl font-semibold">{t.common.nationalHolidays} {y}</h2>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2">{t.table.date}</th>
                <th className="px-3 py-2">{t.table.day}</th>
                <th className="px-3 py-2">{t.table.name}</th>
              </tr>
            </thead>
            <tbody>
              {libur.map((h) => {
                const p = parseISO(h.date)!;
                return (
                  <tr key={`${h.date}-${h.name}`} className="border-t border-border">
                    <td className="px-3 py-2">
                      <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="text-primary hover:underline">
                        {p.d} {t.common.months[p.m - 1]} {y}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{t.common.days[dayOfWeek(p.y, p.m, p.d)]}</td>
                    <td className="px-3 py-2 font-medium text-red-date">{translateHolidayName(h.name, locale)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {cuti.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 text-xl font-semibold">{t.common.jointLeave} {y}</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {cuti.map((h) => {
              const p = parseISO(h.date)!;
              return (
                <li key={`${h.date}-${h.name}`} className="rounded-md border border-border bg-card px-3 py-2 text-sm">
                  <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="hover:text-primary">
                    <strong className="text-cuti-bersama">{p.d} {t.common.months[p.m - 1]}</strong> {translateHolidayName(h.name, locale)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <LongWeekendTable year={y} holidays={holidays} />

      {longWeekends.length > 0 && (
        <section className="mb-8 rounded-lg border border-border bg-card p-4">
          <h2 className="mb-2 text-lg font-semibold">{t.common.longWeekend} {y}</h2>
          <ul className="grid gap-1.5 text-sm sm:grid-cols-2">
            {longWeekends.map((h) => {
              const p = parseISO(h.date)!;
              return (
                <li key={h.date}>
                  <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="hover:text-primary">
                    <strong>{t.common.days[dayOfWeek(p.y, p.m, p.d)]} {p.d} {t.common.months[p.m - 1]}</strong>: {translateHolidayName(h.name, locale)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">{DISCLAIMER_SKB}</p>

      <div className="mt-6">
        <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{t.common.otherYears}</h2>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_YEARS.map((yr) => (
            <Link
              key={yr}
              to={href(`/tanggal-merah/${yr}`) as never}
              className={"rounded-md border border-border px-3 py-1 text-sm hover:bg-accent " + (yr === y ? "bg-accent font-semibold" : "")}
            >
              {yr}
            </Link>
          ))}
        </div>
      </div>
      <CrossLinks year={y} current="tanggal" />

      <section className="mt-8">
        <h2 className="mb-3 text-lg font-semibold">{t.faq.title(`${t.common.redDates} ${y}`)}</h2>
        <div className="space-y-3">
          {faq.map((f) => (
            <div key={f.q} className="rounded-md border border-border bg-card p-3">
              <p className="text-sm font-semibold">{f.q}</p>
              <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}